import { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Trash2, Loader2, Mail, CheckCircle, XCircle, UserCheck, ClipboardList, ShieldAlert, RefreshCw, ArrowRight, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRuntimeAdvisoriesFlag } from '@/hooks/useRuntimeAdvisoriesFlag';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAppShellSummary } from '@/hooks/useAppShellSummary';
import { resolveActionUrl } from '@/lib/safeActionUrl';

interface Notification {
  id: string;
  title: string;
  message: string | null;
  type: string;
  status: string;
  action_url: string | null;
  created_at: string;
  delivery_kind?: string | null;
}

interface NotificationBellProps {
  variant?: 'default' | 'vendor';
}

export function NotificationBell({ variant = 'default' }: NotificationBellProps) {
  const { user, organization, isMspContext, mspHomeOrg } = useOrganization();
  // ENT-5889: runtime_advisory alerts are exposure-gated by the org's runtime_advisories flag.
  const runtimeAdvisoriesFlag = useRuntimeAdvisoriesFlag();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadOverride, setUnreadOverride] = useState<number | null>(null);
  const { activeAlertCount, unreadNotificationCount } = useAppShellSummary({
    organizationId: organization?.id,
    agentOrganizationId: isMspContext ? mspHomeOrg?.id : undefined,
    userId: user?.id,
    deferUntilIdle: true,
  });

  const isUnread = (notification: Notification) => notification.status === 'unread';
  const isUserNotification = (notification: Notification) =>
    (notification.delivery_kind ?? 'user_notification') === 'user_notification';

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      let userNotificationsQuery = supabase
        .from('notification_deliveries')
        .select('*')
        .eq('delivery_kind', 'user_notification')
        .order('created_at', { ascending: false })
        .limit(20);

      if (isMspContext && organization?.id) {
        // When impersonating an MSP customer, only show notifications for that organization
        userNotificationsQuery = userNotificationsQuery.eq('organization_id', organization.id);
      } else {
        // Normal mode: show user's own notifications
        userNotificationsQuery = userNotificationsQuery.eq('user_id', user.id);
      }

      const orgAlertsQuery = organization?.id
        ? supabase
            .from('notification_deliveries')
            .select('*')
            .eq('delivery_kind', 'org_alert')
            .order('created_at', { ascending: false })
            .limit(20)
            .eq('organization_id', organization.id)
        : Promise.resolve({ data: [], error: null });

      const [
        { data: userNotifications, error: userNotificationsError },
        { data: orgAlerts, error: orgAlertsError },
      ] = await Promise.all([userNotificationsQuery, orgAlertsQuery]);

      if (userNotificationsError) throw userNotificationsError;
      if (orgAlertsError) throw orgAlertsError;

      const mergedNotifications = [...(userNotifications || []), ...(orgAlerts || [])]
        .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
        .slice(0, 20);

      setNotifications(mergedNotifications);
      setUnreadOverride(null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isMspContext, organization?.id, user?.id]);

  useEffect(() => {
    if (!user?.id || !isOpen) {
      return;
    }

    void fetchNotifications();
    
    // Subscribe to realtime updates - only for user's own notifications
    const channel = supabase
      .channel(`notification-deliveries:${isMspContext && organization?.id ? organization.id : user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_deliveries',
          filter: isMspContext && organization?.id
            ? `organization_id=eq.${organization.id}`
            : `user_id=eq.${user.id}`,
        },
        () => {
          void fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications, isOpen, user?.id]);

  const markAsRead = async (id: string) => {
    try {
      await supabase
        .from('notification_deliveries')
        .update({ status: 'read', read_at: new Date().toISOString(), read_by: user?.id ?? null })
        .eq('id', id);

      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, status: 'read' } : n))
      );
      setUnreadOverride((prev) => Math.max(0, (prev ?? unreadNotificationCount) - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      let query = supabase
        .from('notification_deliveries')
        .update({ status: 'read', read_at: new Date().toISOString(), read_by: user.id })
        .eq('delivery_kind', 'user_notification')
        .eq('status', 'unread');

      if (isMspContext && organization?.id) {
        query = query.eq('organization_id', organization.id);
      } else {
        query = query.eq('user_id', user.id);
      }

      await query;

      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadOverride(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await supabase
        .from('notification_deliveries')
        .delete()
        .eq('id', id);

      const notification = notifications.find((item) => item.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notification && isUnread(notification)) {
        setUnreadOverride((prev) => Math.max(0, (prev ?? unreadNotificationCount) - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (isUserNotification(notification) && isUnread(notification)) {
      void markAsRead(notification.id);
    }

    const target = resolveActionUrl(notification.action_url, '/alert-centre');

    if (target.kind === 'blocked') {
      return;
    }

    // For access_request notifications, navigate to the requests tab
    if (notification.type === 'access_request' && target.to.includes('/vendor/visibility')) {
      navigate('/vendor/visibility?tab=requests');
    } else if (target.kind === 'external') {
      window.location.assign(target.to);
    } else {
      navigate(target.to);
    }

    setIsOpen(false);
  };

  const unreadCount = unreadOverride ?? unreadNotificationCount;
  const totalAlertCount = unreadCount + activeAlertCount;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'access_request':
        return <Mail className="h-5 w-5 text-primary" />;
      case 'access_granted':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'access_declined':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'supplier_claimed':
      case 'vendor_claimed':
        return (
          <div className="p-1.5 rounded-md bg-amber-100">
            <Bell className="h-4 w-4 text-amber-600" />
          </div>
        );
      case 'team_member_joined':
      case 'team_join_request':
        return <UserCheck className="h-5 w-5 text-blue-500" />;
      case 'questionnaire_request':
      case 'questionnaire_submitted':
        return <ClipboardList className="h-5 w-5 text-indigo-500" />;
      case 'poam_assignment':
      case 'poam_resolved':
      case 'poam_supplier_comment':
        return <ShieldAlert className="h-5 w-5 text-amber-500" />;
      case 'runtime_advisory':
        return <ShieldAlert className="h-5 w-5 text-rose-500" data-testid="runtime-advisory-icon" />;
      case 'billing_support_requested':
      case 'subscription_renewal_requested':
        return <RefreshCw className="h-5 w-5 text-sky-500" />;
      case 'breakglass_request':
      case 'breakglass_approved':
      case 'breakglass_rejected':
      case 'breakglass_revoked':
      case 'breakglass_expired':
        return <Shield className="h-5 w-5 text-amber-600" />;
      default:
        return <Bell className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const isVendor = variant === 'vendor';
  
  const navigateToAlertCentre = () => {
    setIsOpen(false);
    navigate('/alert-centre');
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          aria-label="Notifications"
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {totalAlertCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-medium rounded-full bg-secondary text-secondary-foreground">
              {totalAlertCount > 9 ? '9+' : totalAlertCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-2 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={markAllAsRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              No notifications yet
            </div>
          ) : (
            notifications
              .filter((notification) => runtimeAdvisoriesFlag.enabled || notification.type !== 'runtime_advisory')
              .map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  "flex items-start gap-3 p-3 cursor-pointer",
                  isUnread(notification) && "bg-muted/50"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="shrink-0 mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cn(
                      "text-sm leading-tight",
                      isUnread(notification) && "font-medium"
                    )}>
                      {notification.title}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 hover:bg-destructive/10"
                      onClick={(e) => deleteNotification(notification.id, e)}
                      aria-label="Delete notification"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                  {notification.message && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </p>
                </div>
                {isUnread(notification) && (
                  <div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        {/* Alert Centre link */}
        {!isVendor && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-between"
                onClick={navigateToAlertCentre}
              >
                <span className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Alert Centre
                  {activeAlertCount > 0 && (
                    <span className="h-5 min-w-5 px-1.5 flex items-center justify-center text-xs font-medium rounded-full bg-secondary text-secondary-foreground">
                      {activeAlertCount}
                    </span>
                  )}
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
